/**
 * src/lib/permissions.ts — client permission mirror (FR-010, PRD §5).
 *
 * Snapshot of the 25-permission × 3-role server matrix from
 * `convex/lib/permissions.ts` (PERMS, lines 26-62), restricted to the 23
 * permissions this v.3 UI actually disables-and-tooltips on. The two
 * intentional omissions are `b3_allowlist_modify` and `b5_mode_toggle` —
 * the B3/B5 console surfaces this package does not build (PRD §5, FR-020;
 * `specs/exploration/security_facts.md` §1).
 *
 * This is the DISABLE-SOURCE for the `AccessGate` honest-state primitive.
 * The server is the only real gate (`requirePermission` in
 * `convex/lib/permissions.ts:80-100`); the client mirror exists so the UX
 * can disable-and-tooltip on insufficient authority WITHOUT hiding the
 * affordance (CLAUDE.md §5: "Privileged controls disable-and-tooltip on
 * insufficient role; never hide, never optimistically enable").
 *
 * Drift discipline: every (perm, role) cell here MUST equal the server
 * matrix. The `permissions.contract.test.ts` guardrail (T301/T309) verifies
 * this against the generated `permissions.snapshot.ts` fixture (T302) on
 * every CI build. Editing this file without updating the server is the
 * exact silent-defect class the guardrail catches.
 */

/**
 * Closed 3-literal role union — matches the server's declared union at
 * `convex/lib/permissions.ts:65` verbatim. A fourth role here is a
 * compile-time error AND a guardrail failure.
 */
export type Role = 'admin' | 'editor' | 'viewer';

/**
 * The 23 mirrored permissions (25 server − 2 B3/B5 omissions). Declared
 * `as const` so the value type narrows to the literal tuples and so
 * downstream consumers (AccessGate) get a closed union of permission keys.
 *
 * Cell-by-cell equality with the server is enforced by
 * `tests/guardrails/permissions.contract.test.ts` against the generated
 * `permissions.snapshot.ts`.
 */
export const PERMS = {
  // admin + editor (11 of 11 mirrored — none omitted)
  asset_create:           ['admin', 'editor'],
  asset_update:           ['admin', 'editor'],
  asset_delete:           ['admin', 'editor'],
  asset_tag:              ['admin', 'editor'],
  schedule_create:        ['admin', 'editor'],
  schedule_publish:       ['admin', 'editor'],
  template_manage:        ['admin', 'editor'],
  channel_create:         ['admin', 'editor'],
  podcast_publish:        ['admin', 'editor'],
  media_transform_invoke: ['admin', 'editor'],
  audio_retry:            ['admin', 'editor'],

  // admin-only (11 of 13 mirrored; b3_allowlist_modify + b5_mode_toggle
  // omitted per PRD §5, FR-020 — those surfaces are not built here)
  channel_archive:        ['admin'],
  channel_transfer:       ['admin'],
  channel_mode_set:       ['admin'],
  streamkey_reveal:       ['admin'],
  streamkey_rotate:       ['admin'],
  streamkey_revoke:       ['admin'],
  edge_node_lifecycle:    ['admin'],
  provisioning_run:       ['admin'],
  member_invite:          ['admin'],
  member_remove:          ['admin'],
  member_role_change:     ['admin'],

  // all-roles (1 of 1 mirrored)
  read:                   ['admin', 'editor', 'viewer'],
} as const satisfies Record<string, readonly Role[]>;

/** A mirrored permission key (the closed 23-literal union). */
export type Permission = keyof typeof PERMS;

/**
 * `can(perm, role)` — pure boolean: is `role` in the mirrored allow-list
 * for `perm`?
 *
 * - `null` / `undefined` role  → `false` (no role = no authority).
 * - Unknown `perm`             → `false` (closed-world; no phantom grants).
 * - Otherwise                  → `roles.includes(role)`.
 *
 * The function is the source AccessGate consults for the disable-or-not
 * UX decision. The server still re-checks on every mutation — this is
 * the honest UI shadow of that real gate, NOT a replacement for it.
 */
export function can(
  perm: string,
  role: Role | string | null | undefined,
): boolean {
  if (!role) return false;
  const allowed = (PERMS as Record<string, readonly string[]>)[perm];
  if (!allowed) return false;
  return allowed.includes(role);
}
